/**
 * Insights content hub.
 *
 * Data-driven articles for the lightweight /insights hub. Each entry renders
 * both a card on the overview page (/insights) and a full article page
 * (/insights/<slug>). Keep this file the single source of truth: the sitemap
 * generator (scripts/generate-sitemap.js) reads the `slug` values from here so
 * every published insight is automatically added to sitemap.xml and the
 * react-snap prerender list on the next build.
 *
 * Content blocks are intentionally simple so writing a new article never
 * requires touching component code:
 *   { type: 'paragraph', text }
 *   { type: 'heading', text }
 *   { type: 'list', items: [] }
 *   { type: 'quote', text }
 *   { type: 'image', src, alt, caption }
 *
 * Images reuse the existing project artwork under src/images via the same
 * require.context pattern as src/data/projects.js. `resolveAsset` returns an
 * empty string when a file is missing so the UI can guard on it.
 */

const assetContext = require.context('../images', true, /\.(png|jpe?g|webp)$/);

const resolveAsset = (relativePath) => {
  const normalized = relativePath.replace(/^\.?\//, '');
  try {
    return assetContext(`./${normalized}`);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`[insights] Asset not found: ${relativePath}`);
    return '';
  }
};

export const insights = [
  {
    slug: 'branding-basics-for-small-businesses',
    title: 'Branding basics for small businesses',
    description:
      'What a brand actually is beyond a logo, and the small set of decisions that make a young business look consistent, trustworthy and memorable.',
    date: '2026-02-10',
    displayDate: 'February 2026',
    readTime: '6 min read',
    tag: 'Branding',
    cover: resolveAsset('charged-logo-and-ads/project-10-img-1.png'),
    content: [
      {
        type: 'paragraph',
        text: 'Most small businesses think a brand is a logo. It is not. A logo is one asset inside a brand — the brand is the consistent impression people build up after every touchpoint: your website, your invoice, your Instagram grid, the way you answer an email. If those touchpoints feel like they come from five different companies, you look bigger in your own head than you do in the customer\'s.',
      },
      {
        type: 'heading',
        text: 'Start with three decisions, not thirty',
      },
      {
        type: 'paragraph',
        text: 'You do not need a 60-page brand book on day one. You need a small number of decisions you can actually stick to. Pick a primary colour and one accent, one display font for headlines and one readable font for body text, and a short list of words that describe your tone. That is enough to make everything you produce feel related.',
      },
      {
        type: 'list',
        items: [
          'Colour: one dominant colour plus one accent. Timid, evenly-spread palettes read as generic.',
          'Type: one characterful display font, one clean body font. Two is plenty.',
          'Tone: three or four adjectives (e.g. "warm, direct, a little playful") to guide copy.',
        ],
      },
      {
        type: 'image',
        src: resolveAsset('becoland-logo-animation/project-5-img-1.png'),
        alt: 'Logo and identity design applied consistently across a brand system',
        caption: 'One identity system, applied consistently across every touchpoint.',
      },
      {
        type: 'heading',
        text: 'Consistency beats cleverness',
      },
      {
        type: 'paragraph',
        text: 'A slightly boring identity used consistently will always outperform a brilliant identity used once and then abandoned. Repetition is what turns a set of design choices into recognition. Use the same colours, the same spacing and the same voice everywhere, and people start to remember you after far fewer impressions.',
      },
      {
        type: 'quote',
        text: 'A brand is what people expect from you before you say a word.',
      },
      {
        type: 'heading',
        text: 'When to invest in a designer',
      },
      {
        type: 'paragraph',
        text: 'DIY is fine while you are testing an idea. The moment the business is real — you are spending on ads, pitching bigger clients, or opening a storefront — inconsistent branding starts to cost you money in lost trust. That is usually the right time to bring in a designer to lock the system down so every future asset stays on-brand without guesswork.',
      },
    ],
  },
  {
    slug: 'what-motion-design-adds-to-a-brand',
    title: 'What motion design actually adds to a brand',
    description:
      'Motion is not decoration. Used well, it guides attention, explains complex ideas fast, and makes a brand feel alive across social, web and video.',
    date: '2026-03-05',
    displayDate: 'March 2026',
    readTime: '5 min read',
    tag: 'Motion',
    cover: resolveAsset('multipharma-motion-graphics/project-3-img-1.png'),
    content: [
      {
        type: 'paragraph',
        text: 'Motion design is often treated as a finishing touch — a logo that spins, a button that bounces. That undersells it. Movement is one of the fastest ways the human eye processes meaning, which makes motion a practical tool for directing attention and explaining things words alone struggle with.',
      },
      {
        type: 'heading',
        text: 'Motion guides the eye',
      },
      {
        type: 'paragraph',
        text: 'A static layout asks the viewer to figure out where to look. A well-timed animation makes that decision for them: reveal the headline first, then the supporting line, then the call to action. That sequence is a tiny piece of storytelling, and it happens in under a second.',
      },
      {
        type: 'heading',
        text: 'It explains complexity fast',
      },
      {
        type: 'paragraph',
        text: 'Some ideas are simply hard to show as a still image — a process with steps, a product that transforms, data that changes over time. A short animated explainer can compress a paragraph of text into a few seconds that people actually watch to the end.',
      },
      {
        type: 'image',
        src: resolveAsset('multipharma-motion-graphics/project-3-img-2.png'),
        alt: 'Frame from a branded motion graphics sequence',
        caption: 'A single frame from a branded motion sequence — designed to read in motion, not just as a still.',
      },
      {
        type: 'list',
        items: [
          'Onboarding: show the flow instead of describing it.',
          'Social: motion stops the scroll where a static post gets skipped.',
          'Product: reveal features in the order that tells the best story.',
        ],
      },
      {
        type: 'heading',
        text: 'Restraint is the skill',
      },
      {
        type: 'paragraph',
        text: 'The hard part of motion is not making things move — it is knowing when to stop. Everything animating at once is noise. The best motion work feels almost invisible: it makes the experience smoother without ever calling attention to the animation itself.',
      },
    ],
  },
  {
    slug: 'website-that-actually-converts',
    title: 'Designing a website that actually converts',
    description:
      'A good-looking site is not the goal — a site that turns visitors into enquiries is. Here are the UX fundamentals that move the needle.',
    date: '2026-04-02',
    displayDate: 'April 2026',
    readTime: '7 min read',
    tag: 'Web / UX',
    cover: resolveAsset('final-thesis-project/project-1-img-13.png'),
    content: [
      {
        type: 'paragraph',
        text: 'It is easy to fall in love with a beautiful website and forget what it is for. For most businesses the job of the site is simple: help the right visitor understand what you do and take one clear next step. Everything else is in service of that.',
      },
      {
        type: 'heading',
        text: 'One primary action per page',
      },
      {
        type: 'paragraph',
        text: 'Every page should have a single, obvious thing you want the visitor to do — book a call, view work, send an enquiry. When a page offers five equally weighted options, people tend to pick none. Decide the priority action, make it visually louder than everything else, and repeat it as the visitor scrolls.',
      },
      {
        type: 'heading',
        text: 'Speak to the visitor, not about yourself',
      },
      {
        type: 'paragraph',
        text: 'A common mistake is a homepage that only talks about the company. Visitors care first about their own problem. Lead with what you solve and who it is for, then back it up with proof — work, results, testimonials — before asking for anything.',
      },
      {
        type: 'image',
        src: resolveAsset('pxl-esports-case/project-20-img-1.png'),
        alt: 'Web interface layout guiding the visitor toward a clear action',
        caption: 'A layout built around one clear action, with proof supporting the decision.',
      },
      {
        type: 'list',
        items: [
          'Above the fold: say what you do and for whom in one line.',
          'Proof next: selected work, recognisable clients, or concrete outcomes.',
          'Then the ask: a single, unmissable call to action.',
        ],
      },
      {
        type: 'heading',
        text: 'Speed and clarity are UX',
      },
      {
        type: 'paragraph',
        text: 'A stunning site that loads slowly on a phone loses people before they see it. Performance, legible type, generous tap targets and a layout that survives a small screen are not separate from design — they are the parts of design the visitor feels most. Get those right and the beautiful details finally get a chance to land.',
      },
      {
        type: 'quote',
        text: 'If a visitor has to think about how to use your site, the design is doing the opposite of its job.',
      },
    ],
  },
];

export const getInsightBySlug = (slug) =>
  insights.find((entry) => entry.slug === slug) || null;

export default insights;
